import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, ChevronRight, Info, ShieldCheck, AlertTriangle } from "lucide-react";
import { fmtEUR, fmtInt } from "@/components/Common";

const CUTOVER = "2026-08-16";

export function ImportWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyImported, setAlreadyImported] = useState(null);

  const reset = () => { setStep(1); setFile(null); setPreview(null); setConfirmed(false); setResult(null); setAlreadyImported(null); };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const runPreview = async () => {
    if (!file) return toast.error("Seleziona un file");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("assumed_year", String(year));
      const { data } = await api.post("/sales/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.file?.already_imported) {
        // fetch batch info
        try {
          const { data: batches } = await api.get("/sales/imports");
          const b = batches.find((x) => x.file_sha256 === data.file.sha256);
          setAlreadyImported(b);
        } catch {}
      }
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore preview");
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("assumed_year", String(year));
      const partials = (preview.cutover?.pre_cutover_rows > 0 || preview.cutover?.post_cutover_rows > 0)
        ? preview.totals.date_range[1] : "";
      // Il 18/8 è l'ultima data ed è dichiarata parziale (export intermittente).
      // Frontend passa l'ultima data come partial di default. Editabile in step 2 in v2.
      fd.append("partial_days", preview.totals.date_range?.[1] || "");
      const { data } = await api.post("/sales/import-commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore commit"); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-serif">Import CSV vendite — {step===1?"Upload":step===2?"Preview":"Conferma"}</DialogTitle>
          <div className="flex gap-2 mt-2 text-xs">
            {[1,2,3].map(n=>(
              <span key={n} className={`px-2 py-0.5 rounded ${step===n?"bg-neutral-900 text-[#FDFBF7]":step>n?"bg-emerald-100 text-emerald-800":"bg-neutral-100 text-neutral-500"}`}>
                {n}. {n===1?"Upload":n===2?"Preview":"Conferma"}
              </span>
            ))}
          </div>
        </DialogHeader>

        {step===1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <Input type="file" accept=".csv" onChange={(e)=>setFile(e.target.files?.[0]||null)} data-testid="wizard-file-input" />
            {file && <div className="text-xs text-neutral-500">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
            <div><Label>Anno dei dati</Label><Input type="number" value={year} onChange={(e)=>setYear(Number(e.target.value)||2026)} data-testid="wizard-year" /></div>
            <div className="text-[11px] text-neutral-500 bg-[#F3EDE1] rounded p-2 flex gap-2"><Info size={12} className="mt-0.5"/><span>La preview non modifica alcun dato. Riconoscimento automatico formato/delimiter/encoding.</span></div>
            <DialogFooter><Button disabled={!file||loading} onClick={runPreview} data-testid="wizard-preview-btn">{loading?"Analisi…":"Genera preview"} <ChevronRight size={14}/></Button></DialogFooter>
          </div>
        )}

        {step===2 && preview && (
          <div className="space-y-4 text-sm" data-testid="wizard-step-2">
            {alreadyImported && (
              <div className="border border-amber-300 bg-amber-50 rounded p-2 text-xs" data-testid="wizard-already-imported">
                <AlertTriangle size={12} className="inline mr-1 text-amber-700"/>
                <b>Questo file è già stato importato</b> il {new Date(alreadyImported.imported_at).toLocaleString("it-IT")} — batch <code>{alreadyImported.id.slice(0,8)}</code> · {fmtInt(alreadyImported.rows_inserted)} record · {fmtEUR(alreadyImported.total_revenue)}. Il re-commit produrrà 0 nuovi record (idempotenza SHA).
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><b>Formato:</b> {preview.file.detected_format}</div>
              <div><b>Delimiter:</b> <code>{preview.file.detected_delimiter}</code></div>
              <div><b>Encoding:</b> {preview.file.detected_encoding}</div>
              <div><b>SHA:</b> <code>{preview.file.sha256.slice(0,12)}…</code></div>
            </div>

            <div className="border-t border-[#E2D9C8] pt-3">
              <div className="label-eyebrow mb-2">Riepilogo</div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <Kpi label="Righe CSV" val={fmtInt(preview.totals.rows_read)} />
                <Kpi label="Record normalizzati" val={fmtInt(preview.totals.rows_normalized)} />
                <Kpi label="Unità" val={fmtInt(preview.totals.total_quantity)} />
                <Kpi label="Fatturato" val={fmtEUR(preview.totals.total_revenue)} />
                <Kpi label="Prodotti" val={preview.menu_mapping.csv_products_count} />
                <Kpi label="Giorni" val={preview.totals.days_present} />
                <Kpi label="Righe vuote" val={preview.totals.empty_rows} />
                <Kpi label="Warning" val={preview.warnings.length} />
              </div>
              <div className="text-[11px] text-neutral-500 mt-2">Periodo <b>{preview.totals.date_range?.[0]} → {preview.totals.date_range?.[1]}</b></div>
            </div>

            <div className="border-t border-[#E2D9C8] pt-3">
              <div className="label-eyebrow mb-1">Cutover finanziario ({preview.cutover.date})</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-neutral-500">Pre-cutover · storico analitico</div>
                  <div className="font-medium">{preview.cutover.pre_cutover_rows} record · {fmtEUR(preview.cutover.pre_cutover_revenue)}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div className="text-neutral-500">Post-cutover · operational</div>
                  <div className="font-medium">{preview.cutover.post_cutover_rows} record · {fmtEUR(preview.cutover.post_cutover_revenue)}</div>
                </div>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">I dati pre-cutover sono usati solo per analytics e non modificano conti o inventario.</div>
            </div>

            <div className="border-t border-[#E2D9C8] pt-3">
              <div className="label-eyebrow mb-1">Fasce CSV originali</div>
              <table className="w-full text-xs">
                <thead className="text-neutral-500"><tr><th className="text-left">Fascia</th><th className="text-right">Revenue</th><th className="text-right">Qty</th><th className="text-right">Giorni</th></tr></thead>
                <tbody>
                  {preview.bands.map(b=>(
                    <tr key={b.csv_band} className="border-t border-[#E2D9C8]">
                      <td>{b.csv_band}</td>
                      <td className="text-right">{fmtEUR(b.revenue)}</td>
                      <td className="text-right">{fmtInt(b.quantity)}</td>
                      <td className="text-right">{b.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10px] text-neutral-500 mt-1">Mapping operativo: non configurato. Le fasce restano CSV originali.</div>
            </div>

            <div className="border-t border-[#E2D9C8] pt-3">
              <div className="label-eyebrow mb-1">Mapping prodotti</div>
              <div className="text-xs">{preview.menu_mapping.matched} matched · {preview.menu_mapping.ambiguous} ambiguous · <b>{preview.menu_mapping.unmatched} Non collegato</b></div>
              {preview.menu_mapping.notice && <div className="text-[11px] text-neutral-500">{preview.menu_mapping.notice}</div>}
            </div>

            {preview.warnings.length>0 && (
              <div className="border-t border-[#E2D9C8] pt-3">
                <div className="label-eyebrow mb-1">Warning ({preview.warnings.length})</div>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                  {preview.warnings.slice(0,20).map((w,i)=>(
                    <li key={i}><span className={w.level==="warning"?"text-amber-700":"text-neutral-600"}>[{w.code}]</span> {w.message || `${w.product||""} qty=${w.qty} rev=${w.revenue}`} {w.date && `· ${w.date} ${w.band}`}</li>
                  ))}
                </ul>
                <div className="text-[11px] text-neutral-500 mt-1">
                  <b>variante</b>: {preview.non_product.adjustment_quantity_total} rettifiche POS ({preview.non_product.occurrences} occorrenze), esclusa dai prodotti.
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={()=>setStep(1)}>Indietro</Button>
              <Button onClick={()=>setStep(3)} disabled={!preview.commit_ready} data-testid="wizard-to-confirm">Procedi <ChevronRight size={14}/></Button>
            </DialogFooter>
          </div>
        )}

        {step===3 && preview && (
          <div className="space-y-4" data-testid="wizard-step-3">
            {result ? (
              <div className="space-y-2" data-testid="wizard-result">
                <div className="text-emerald-800 font-medium">✓ Importazione completata</div>
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-3 space-y-1">
                  <div>Batch: <code>{result.id.slice(0,12)}…</code></div>
                  <div><b>{fmtInt(result.rows_inserted)}</b> record inseriti · {fmtInt(result.rows_updated)} aggiornati · {fmtInt(result.rows_skipped)} ignorati</div>
                  <div>Quantità totale: <b>{fmtInt(result.total_quantity)}</b> · Fatturato: <b>{fmtEUR(result.total_revenue)}</b></div>
                  <div>Periodo: {result.date_range?.join(" → ")} · Partial: {result.partial_days?.join(", ") || "nessuno"}</div>
                </div>
                <DialogFooter><Button onClick={close}>Chiudi</Button></DialogFooter>
              </div>
            ) : (
              <>
                <div className="bg-[#F3EDE1] border border-[#E2D9C8] rounded p-3 text-sm">
                  <div className="font-medium mb-2">Stai per importare:</div>
                  <ul className="space-y-1 text-xs">
                    <li><b>{fmtInt(preview.totals.rows_normalized)}</b> record prodotto</li>
                    <li><b>{fmtInt(preview.totals.total_quantity)}</b> unità · <b>{fmtEUR(preview.totals.total_revenue)}</b> fatturato</li>
                    <li><b>{preview.menu_mapping.csv_products_count}</b> prodotti · periodo <b>{preview.totals.date_range?.[0]} → {preview.totals.date_range?.[1]}</b></li>
                  </ul>
                  <div className="mt-3 text-[11px] flex gap-2 text-neutral-600"><ShieldCheck size={12} className="mt-0.5 text-emerald-700"/><span>Nessun conto verrà modificato · Nessun movimento finanziario · Nessun inventario scaricato.</span></div>
                </div>
                <label className="flex items-center gap-2 text-sm" data-testid="wizard-confirm-check">
                  <input type="checkbox" checked={confirmed} onChange={(e)=>setConfirmed(e.target.checked)} />
                  Ho verificato la preview e confermo l'importazione
                </label>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setStep(2)}>Indietro</Button>
                  <Button className="bg-emerald-700 hover:bg-emerald-800 text-[#FDFBF7]" disabled={!confirmed||committing} onClick={commit} data-testid="wizard-commit-btn">{committing?"Import…":"Conferma importazione"}</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, val }) {
  return (
    <div className="border border-[#E2D9C8] rounded p-2 bg-white">
      <div className="text-[10px] text-neutral-500 uppercase">{label}</div>
      <div className="font-serif text-lg">{val}</div>
    </div>
  );
}

export function ImportHistory({ refreshKey, onUndone }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales/imports"); setBatches(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);
  const undo = async (b) => {
    if (!window.confirm(`Annullare l'import di ${b.filename}?\n${b.rows_inserted} record verranno rimossi.\nQuesta azione è tracciata in audit.`)) return;
    try { await api.delete(`/sales/imports/${b.id}`); toast.success("Import annullato"); load(); onUndone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore undo"); }
  };
  if (loading) return null;
  if (batches.length===0) return null;
  return (
    <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white mb-6" data-testid="import-history">
      <div className="label-eyebrow mb-2">Storico import vendite</div>
      <table className="w-full text-xs">
        <thead className="text-neutral-500"><tr><th className="text-left">Data</th><th className="text-left">File</th><th className="text-left">Periodo</th><th className="text-right">Righe</th><th className="text-right">Qty</th><th className="text-right">Revenue</th><th></th></tr></thead>
        <tbody>
          {batches.map(b=>(
            <tr key={b.id} className="border-t border-[#E2D9C8]" data-testid={`batch-${b.id}`}>
              <td>{new Date(b.imported_at).toLocaleString("it-IT", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit"})}</td>
              <td>{b.filename}</td>
              <td>{b.date_range?.join(" → ")}</td>
              <td className="text-right">{fmtInt(b.rows_inserted)}</td>
              <td className="text-right">{fmtInt(b.total_quantity)}</td>
              <td className="text-right">{fmtEUR(b.total_revenue)}</td>
              <td className="text-right"><button onClick={()=>undo(b)} className="text-neutral-500 hover:text-red-700 text-[10px]">Annulla</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
